/**
 * DirectMessage — Two-way conversation view between coach and a single client.
 * Uses Supabase `coach_messages` table with real-time subscription.
 * For DMs, recipient_ids contains one client ID.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  TextInput, FlatList, ActivityIndicator, KeyboardAvoidingView, Platform,
} from 'react-native';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { X, Send } from 'lucide-react-native';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface DMMessage {
  id: string;
  coach_id: string;
  recipient_ids: string[];
  content: string;
  sent_at: string;
  read_at?: string | null;
}

interface Props {
  visible: boolean;
  clientId: string;
  clientName: string;
  onClose: () => void;
}

/**
 * Get unread DM count for a given user from a specific sender.
 * Call from client detail to show badge.
 */
export async function getUnreadCount(myId: string, partnerId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('coach_messages')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', partnerId)
      .contains('recipient_ids', [myId])
      .is('read_at', null);
    if (error) return 0;
    return count ?? 0;
  } catch { return 0; }
}

export default function DirectMessage({ visible, clientId, clientName, onClose }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [coachId, setCoachId] = useState('');
  const flatListRef = useRef<FlatList>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  const isDM = (m: { coach_id: string; recipient_ids: string[] }, myId: string, otherId: string): boolean => {
    const rIds = m.recipient_ids ?? [];
    if (rIds.length !== 1) return false;
    return (
      (m.coach_id === myId && rIds[0] === otherId) ||
      (m.coach_id === otherId && rIds[0] === myId)
    );
  };

  const loadMessages = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const myId = user?.id ?? '';
      setCoachId(myId);

      const { data, error } = await supabase
        .from('coach_messages')
        .select('id, coach_id, recipient_ids, content, sent_at, read_at')
        .or(`coach_id.eq.${myId},recipient_ids.cs.{${clientId}}`)
        .order('sent_at', { ascending: true })
        .limit(200);

      if (!error && data) {
        const dms = data.filter((m: any) => isDM(m, myId, clientId));
        setMessages(dms);

        // Mark incoming messages as read
        const unreadIds = dms
          .filter((m) => m.coach_id === clientId && !m.read_at)
          .map((m) => m.id);
        if (unreadIds.length > 0) {
          await supabase
            .from('coach_messages')
            .update({ read_at: new Date().toISOString() })
            .in('id', unreadIds);
        }
      }
    } catch (err) {
      console.warn('[DirectMessage] load error', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Real-time subscription
  useEffect(() => {
    if (!visible || !clientId) return;

    setText('');
    loadMessages();

    // Subscribe to new messages
    const channel = supabase
      .channel(`dm-${clientId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'coach_messages' },
        (payload) => {
          const msg = payload.new as DMMessage;
          if (isDM(msg, coachId, clientId)) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
            setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 100);
            // Auto-mark as read if from client
            if (msg.coach_id === clientId && !msg.read_at) {
              supabase
                .from('coach_messages')
                .update({ read_at: new Date().toISOString() })
                .eq('id', msg.id)
                .then();
            }
          }
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [visible, clientId, coachId, loadMessages]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const msg: DMMessage = {
        id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        coach_id: coachId,
        content: text.trim(),
        recipient_ids: [clientId],
        sent_at: new Date().toISOString(),
        read_at: null,
      };
      const { error } = await supabase.from('coach_messages').insert(msg);
      if (error) console.warn('[DirectMessage] send error', error.message);
      setText('');
      setMessages((prev) => [...prev, msg]);
      setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 100);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const renderMessage = ({ item }: { item: DMMessage }) => {
    const isCoach = item.coach_id === coachId;
    return (
      <View style={[styles.bubble, isCoach ? styles.bubbleRight : styles.bubbleLeft,
        { backgroundColor: isCoach ? c.accent : c.surface }]}>
        {!isCoach && (
          <Text style={[styles.senderLabel, { color: c.textSecondary }]}>{clientName}</Text>
        )}
        <Text style={[styles.bubbleText, { color: isCoach ? '#fff' : c.text }]}>
          {item.content}
        </Text>
        <View style={styles.metaRow}>
          <Text style={[styles.timestamp, { color: isCoach ? 'rgba(255,255,255,0.7)' : c.textTertiary }]}>
            {formatDate(item.sent_at)} {formatTime(item.sent_at)}
          </Text>
          {isCoach && item.read_at && (
            <Text style={[styles.readReceipt, { color: 'rgba(255,255,255,0.6)' }]}>✓ Read</Text>
          )}
        </View>
      </View>
    );
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <KeyboardAvoidingView
        style={styles.overlay}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                {clientName}
              </Text>
              <Text style={[styles.subtitle, { color: c.textSecondary }]}>Direct Message</Text>
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <X size={22} color={c.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Messages */}
          {loading ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator color={c.accent} />
            </View>
          ) : (
            <FlatList
              ref={flatListRef}
              data={messages}
              keyExtractor={(m) => m.id}
              renderItem={renderMessage}
              contentContainerStyle={styles.messageList}
              keyboardShouldPersistTaps="handled"
              onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: false })}
              ListEmptyComponent={
                <Text style={[styles.emptyText, { color: c.textTertiary }]}>
                  No messages yet. Start the conversation!
                </Text>
              }
            />
          )}

          {/* Input */}
          <View style={[styles.inputRow, { borderColor: c.border }]}>
            <TextInput
              style={[styles.input, { color: c.text, backgroundColor: c.surface, borderColor: c.border }]}
              placeholder="Type a message..."
              placeholderTextColor={c.textTertiary}
              value={text}
              onChangeText={setText}
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[styles.sendBtn, { backgroundColor: text.trim() ? c.accent : c.border }]}
              onPress={handleSend}
              disabled={sending || !text.trim()}
            >
              {sending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Send size={18} color="#fff" />
              )}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'flex-end',
  },
  modal: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderWidth: 1,
    borderBottomWidth: 0,
    maxHeight: '90%',
    minHeight: '60%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 0.5,
  },
  subtitle: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 1,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  messageList: {
    padding: 16,
    paddingTop: 4,
    flexGrow: 1,
  },
  bubble: {
    maxWidth: '78%',
    borderRadius: 2,
    padding: 10,
    paddingHorizontal: 14,
    marginBottom: 8,
  },
  bubbleLeft: {
    alignSelf: 'flex-start',
    borderBottomLeftRadius: 4,
  },
  bubbleRight: {
    alignSelf: 'flex-end',
    borderBottomRightRadius: 4,
  },
  senderLabel: {
    fontSize: 11,
    fontWeight: '700',
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  bubbleText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  timestamp: {
    fontSize: 11,
    fontWeight: '500',
    textAlign: 'right',
  },
  readReceipt: {
    fontSize: 11,
    fontWeight: '600',
  },
  emptyText: {
    textAlign: 'center',
    marginTop: 40,
    fontSize: 14,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 12,
    gap: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 2,
    padding: 10,
    fontSize: 14,
    maxHeight: 100,
    textAlignVertical: 'top',
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
